import React, { useState, useEffect, useCallback } from 'react';
import { foundationAPI, settlingAPI, jobApiError } from '../services/api';
import { FiAlertCircle, FiCheckCircle } from 'react-icons/fi';

// Foundation step 3 (brainstorms/2026-10-17-foundation-enforcement.md, Q11/Q12):
// the client complaints register with its working-day clock, and welfare check-ins.

const muted = { color: '#64748b', fontSize: 12 };
const row = { padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: 13 };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const Err = ({ text }) => (text ? <div className="error-message" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}><FiAlertCircle size={16} />{text}</div> : null);
const Ok = ({ text }) => (text ? <div style={{ color: '#15803d', fontSize: 13, margin: '8px 0', display: 'flex', gap: 6, alignItems: 'center' }}><FiCheckCircle />{text}</div> : null);
const KINDS = [['', 'Not in the VMS yet'], ['STUDENT', 'Student'], ['JOB', 'Job seeker'], ['TOURIST', 'Tourist'], ['MEDICAL', 'Medical client']];
const SECTIONS = { 1: 'Time', 2: 'Speaking and listening', 3: 'Communication', 4: 'Confidentiality and honesty', 5: 'Ownership and transparency',
  6: 'Customer excellence', 7: 'Planning rhythm', 8: 'Integrity of documents and advice', 9: 'Money', 10: 'Conflicts of interest and gifts',
  11: 'Professional boundaries and client care', 12: 'Official materials', 13: 'Availability and handover', 14: 'Raising concerns safely' };

// ─── Complaints ────────────────────────────────────────────────────────────
const ClientPicker = ({ value, onChange }) => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  useEffect(() => {
    if (!value.client_kind || q.trim().length < 2) { setHits([]); return undefined; }
    const t = setTimeout(() => settlingAPI.searchClients({ kind: value.client_kind, q }).then((r) => setHits(r.data)).catch(() => setHits([])), 250);
    return () => clearTimeout(t);
  }, [q, value.client_kind]);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <select value={value.client_kind} onChange={(e) => onChange({ client_kind: e.target.value, client_id: '', client_name: '' })}>
        {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {value.client_kind ? (
        value.client_id ? (
          <span style={{ alignSelf: 'center' }}>{value.client_name} <button className="btn-secondary" onClick={() => onChange({ ...value, client_id: '', client_name: '' })}>Change</button></span>
        ) : (
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <input style={{ width: '100%' }} placeholder="Search name or reference" value={q} onChange={(e) => setQ(e.target.value)} />
            {hits.length > 0 && (
              <div style={{ position: 'absolute', zIndex: 5, background: 'var(--card-bg, #fff)', border: '1px solid #e2e8f0', width: '100%' }}>
                {hits.map((h) => <div key={h.id} style={{ padding: 6, cursor: 'pointer' }} onClick={() => { onChange({ ...value, client_id: h.id, client_name: h.name }); setQ(''); }}>{h.name} · {h.ref}</div>)}
              </div>
            )}
          </div>
        )
      ) : (
        <input style={{ flex: 1, minWidth: 200 }} placeholder="Client's name" value={value.client_name} onChange={(e) => onChange({ ...value, client_name: e.target.value })} />
      )}
    </div>
  );
};

const ComplaintCard = ({ c, data, people, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ note: '', resolution: '', root_cause: '', client_informed_on: today(), responsible: c.responsible_id || '',
    person: '', rule_section: '', level: '1' });
  const [err, setErr] = useState('');
  const act = async (op, body) => { try { await foundationAPI.complaintAction(c.id, op, body); setErr(''); onChanged(); } catch (e) { setErr(jobApiError(e)); } };
  const late = c.ack_overdue || c.resolve_overdue;
  return (
    <div style={row}>
      <div style={{ display: 'flex', gap: 8, cursor: 'pointer', alignItems: 'center' }} onClick={() => setOpen(!open)}>
        <strong style={{ flex: 1 }}>{c.severity === 'SERIOUS' ? 'SERIOUS · ' : ''}{c.client_name}{c.client_ref ? ` (${c.client_ref})` : ''}</strong>
        <span style={muted}>{c.channel_label} · received {fmtDate(c.received_on)} · {c.responsible || 'no one responsible'}</span>
        <span className="status-pill" style={{ color: late ? '#b91c1c' : c.status === 'CLOSED' ? '#15803d' : '#b45309' }}>
          {c.status === 'CLOSED' ? (c.closed_on_time ? 'Closed on time' : 'Closed late') : c.ack_overdue ? 'Not acknowledged' : c.resolve_overdue ? 'Overdue' : c.status_label}
        </span>
      </div>
      {open && (
        <div style={{ marginTop: 6 }}>
          <Err text={err} />
          <div><strong>What happened:</strong> {c.what_happened}</div>
          <div style={muted}>Logged by {c.logged_by}. Acknowledge by {fmtDate(c.ack_due)} · resolve by {fmtDate(c.resolve_due)} (working days, Zimbabwe holidays excluded). Open {c.days_open} day(s).</div>
          {c.ack_note && <div><strong>Acknowledged:</strong> {c.ack_note}</div>}
          {c.resolution && <div><strong>Resolution:</strong> {c.resolution} · root cause: {c.root_cause_label} · client told {fmtDate(c.client_informed_on)}</div>}
          {c.conduct_case_id && <div style={muted}>A conduct case was opened from this complaint (Team & Rules → Conduct).</div>}
          {c.can_handle && c.status === 'NEW' && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input style={{ flex: 1 }} placeholder="How the client was told we are on it" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
              <button className="btn-secondary" disabled={!f.note.trim()} onClick={() => act('acknowledge', { note: f.note })}>Acknowledge</button>
            </div>
          )}
          {c.can_handle && c.status !== 'CLOSED' && (
            <div style={{ marginTop: 8 }}>
              <textarea rows={2} style={{ width: '100%' }} placeholder="How it was resolved" value={f.resolution} onChange={(e) => setF({ ...f, resolution: e.target.value })} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                <select value={f.root_cause} onChange={(e) => setF({ ...f, root_cause: e.target.value })}>
                  <option value="">Root cause…</option>{data.root_causes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label style={muted}>Client told on <input type="date" value={f.client_informed_on} onChange={(e) => setF({ ...f, client_informed_on: e.target.value })} /></label>
                <button className="btn-primary" disabled={f.resolution.trim().length < 10 || !f.root_cause} onClick={() => act('close', f)}>Close complaint</button>
              </div>
            </div>
          )}
          {c.can_manage && c.status !== 'CLOSED' && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <select value={f.responsible} onChange={(e) => setF({ ...f, responsible: e.target.value })}>
                <option value="">Responsible…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
              </select>
              <button className="btn-secondary" disabled={!f.responsible || Number(f.responsible) === c.responsible_id} onClick={() => act('assign', { responsible: f.responsible })}>Reassign</button>
            </div>
          )}
          {c.can_manage && c.status === 'CLOSED' && <button className="btn-secondary" style={{ marginTop: 6 }} onClick={() => act('reopen')}>Reopen</button>}
          {c.can_handle && !c.conduct_case_id && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={muted}>A team rule was broken?</span>
              <select value={f.person} onChange={(e) => setF({ ...f, person: e.target.value })}>
                <option value="">Who…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select value={f.rule_section} onChange={(e) => setF({ ...f, rule_section: e.target.value })}>
                <option value="">Section…</option>{Object.entries(SECTIONS).map(([n, l]) => <option key={n} value={n}>{n}. {l}</option>)}
              </select>
              <select value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })}>{[1, 2, 3, 4].map((n) => <option key={n} value={n}>Level {n}</option>)}</select>
              <button className="btn-secondary" disabled={!f.person || !f.rule_section} onClick={() => act('conduct', { person: f.person, rule_section: f.rule_section, level: f.level })}>Open conduct case</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Complaints() {
  const blank = { client_kind: '', client_id: '', client_name: '', channel: 'PHONE', severity: 'NORMAL', what_happened: '', received_on: today() };
  const [show, setShow] = useState('open');
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [f, setF] = useState(blank);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const load = useCallback(() => foundationAPI.complaints(show).then((r) => setData(r.data)).catch((e) => setErr(jobApiError(e))), [show]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { foundationAPI.people().then((r) => setPeople(r.data.filter((p) => p.role !== 'CSO'))).catch(() => {}); }, []);
  const log = async () => {
    try { await foundationAPI.logComplaint(f); setF(blank); setErr(''); setOk('Logged. The COO and CEO have been told.'); load(); } catch (e) { setOk(''); setErr(jobApiError(e)); }
  };
  return (
    <div className="page-container" style={{ padding: 24 }}>
      <h1>Client complaints</h1>
      <div style={muted}>Acknowledge within 1 working day (team rule 6.1), resolve within 10 working days (KPI CEO4). The COO is responsible, the CEO accountable. A feedback survey scored 1 or 2 out of 5 opens a complaint by itself.</div>
      <Err text={err} /><Ok text={ok} />
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Log a complaint</span></div>
        <ClientPicker value={f} onChange={(v) => setF({ ...f, ...v })} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })}>
            {(data?.channels || []).filter(([v]) => v !== 'FEEDBACK').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })}><option value="NORMAL">Normal</option><option value="SERIOUS">Serious</option></select>
          <label style={muted}>Received <input type="date" value={f.received_on} max={today()} onChange={(e) => setF({ ...f, received_on: e.target.value })} /></label>
        </div>
        <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="What happened, in the client's words" value={f.what_happened} onChange={(e) => setF({ ...f, what_happened: e.target.value })} />
        <div style={{ textAlign: 'right' }}>
          <button className="btn-primary" disabled={f.what_happened.trim().length < 10 || (f.client_kind ? !f.client_id : !f.client_name.trim())} onClick={log}>Log complaint</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, margin: '8px 0' }}>
        {[['open', 'Open'], ['closed', 'Closed'], ['all', 'All']].map(([k, l]) => <button key={k} className={show === k ? 'btn-primary' : 'btn-secondary'} onClick={() => setShow(k)}>{l}</button>)}
      </div>
      <div className="cv-subsection">
        {data && data.complaints.length === 0 && <div className="cv-subsection-empty">None.{!data.can_manage && ' You see the complaints you logged or are responsible for.'}</div>}
        {data && data.complaints.map((c) => <ComplaintCard key={c.id} c={c} data={data} people={people} onChanged={load} />)}
      </div>
    </div>
  );
}

// ─── Welfare check-ins (CRM side) ──────────────────────────────────────────
const METHODS = [['CALL', 'Call'], ['VISIT', 'Visit'], ['WHATSAPP', 'WhatsApp']];
const WELLBEING = [['OK', 'OK'], ['CONCERN', 'Concern'], ['URGENT', 'Urgent']];

export const CheckInForm = ({ c, record, onDone }) => {
  const [f, setF] = useState({ method: 'CALL', wellbeing: 'OK', note: '', addon_interest: false, addon_note: '' });
  const [err, setErr] = useState('');
  const save = async () => { try { await record(c.id, f); setErr(''); onDone(); } catch (e) { setErr(jobApiError(e)); } };
  return (
    <div style={{ marginTop: 6 }}>
      <Err text={err} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>{METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select value={f.wellbeing} onChange={(e) => setF({ ...f, wellbeing: e.target.value })}>{WELLBEING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input style={{ flex: 1, minWidth: 200 }} placeholder={f.wellbeing === 'OK' ? 'Note (optional)' : 'Describe the concern'} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4, alignItems: 'center' }}>
        <label style={{ fontSize: 13 }}><input type="checkbox" checked={f.addon_interest} onChange={(e) => setF({ ...f, addon_interest: e.target.checked })} /> Interested in another service</label>
        {f.addon_interest && <input style={{ flex: 1, minWidth: 200 }} placeholder="Which service?" value={f.addon_note} onChange={(e) => setF({ ...f, addon_note: e.target.value })} />}
        <button className="btn-primary" disabled={f.wellbeing !== 'OK' && f.note.trim().length < 5} onClick={save}>Log check-in</button>
      </div>
      {f.wellbeing === 'URGENT' && <div style={{ color: '#b91c1c', fontSize: 12 }}>Urgent goes straight to the COO as an escalation.</div>}
    </div>
  );
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TermDates = () => {
  const blank = { country: '', label: '', month: '9', day: '25' };
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(blank);
  const [err, setErr] = useState('');
  const load = useCallback(() => settlingAPI.termDates().then((r) => setRows(r.data.results ?? r.data)).catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn) => { try { await fn(); setErr(''); load(); } catch (e) { setErr(jobApiError(e)); } };
  return (
    <div className="cv-subsection" style={{ marginTop: 16 }}>
      <div className="cv-subsection-title"><span>Term start dates (for students' term check-ins)</span></div>
      <div style={muted}>Every year on these dates. "Any other country" applies where a country has no dates of its own.</div>
      <Err text={err} />
      {rows.map((t) => (
        <div key={t.id} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{t.country || 'Any other country'} · {t.label}</span>
          <span>{t.day} {MONTHS[t.month - 1]}</span>
          <button className="btn-secondary" onClick={() => window.confirm('Remove this term date?') && run(() => settlingAPI.deleteTermDate(t.id))}>Remove</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        <input placeholder="Country (blank = any other)" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} />
        <input placeholder="Label, e.g. Autumn term" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <input style={{ width: 60 }} type="number" min="1" max="31" value={f.day} onChange={(e) => setF({ ...f, day: e.target.value })} />
        <select value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })}>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
        <button className="btn-secondary" disabled={!f.label.trim()} onClick={() => run(() => settlingAPI.addTermDate(f)).then(() => setF(blank))}>Add</button>
      </div>
    </div>
  );
};

export const CheckInsPanel = ({ canManage }) => {
  const [show, setShow] = useState('due');
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => settlingAPI.checkIns({ show }).then((r) => setRows(r.data)).catch((e) => setErr(jobApiError(e))), [show]);
  useEffect(() => { load(); }, [load]);
  return (
    <div style={{ marginTop: 12 }}>
      <div style={muted}>Students: week 1, month 1, then each term start until alumni. Job seekers: week 1, month 1, then every 6 months. Tourists and medical clients: one check-in after the trip. Logged within 3 days of the due date counts for KPI CSO4.</div>
      <div style={{ display: 'flex', gap: 6, margin: '8px 0' }}>
        {[['due', 'Due and coming up'], ['done', 'Logged']].map(([k, l]) => <button key={k} className={show === k ? 'btn-primary' : 'btn-secondary'} onClick={() => setShow(k)}>{l}</button>)}
      </div>
      <Err text={err} />
      {rows && rows.length === 0 && <div className="cv-subsection-empty">Nothing here.</div>}
      {rows && rows.map((c) => (
        <div key={c.id} style={row}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: c.done_at ? 'default' : 'pointer' }} onClick={() => !c.done_at && setOpenId(openId === c.id ? null : c.id)}>
            <strong style={{ flex: 1 }}>{c.client_name} <span style={muted}>({c.client_kind_label}{c.place ? `, ${c.place}` : ''})</span></strong>
            <span style={muted}>{c.label} · due {fmtDate(c.due_date)} · {c.officer || 'no officer'}</span>
            {c.done_at
              ? <span className="status-pill" style={{ color: c.wellbeing === 'OK' ? '#15803d' : '#b91c1c' }}>{c.wellbeing} · {c.done_by}</span>
              : <span className="status-pill" style={{ color: c.overdue ? '#b91c1c' : '#b45309' }}>{c.overdue ? 'Overdue' : 'Due'}</span>}
          </div>
          {c.done_at && c.note && <div style={muted}>{c.note}</div>}
          {openId === c.id && <CheckInForm c={c} record={settlingAPI.recordCheckIn} onDone={() => { setOpenId(null); load(); }} />}
        </div>
      ))}
      {canManage && <TermDates />}
    </div>
  );
};
